import {useMemo, useState} from 'react';
import {BaseInputProps} from '../..';

import {
  createSpotlight,
  Spotlight,
  SpotlightActionData,
} from '@mantine/spotlight';

import AddressModal from './components/address-modal';
import InputButton from '../input-button';
import {useController, useFormContext} from 'react-hook-form';
import {IoAdd, IoSearch} from 'react-icons/io5';
import {CenterdColumn} from './styles';
import Map from './components/map';
import {AddressProps} from 'modules/addresses/types';
import {AllKeys} from 'shared/types/key-manipulation';
import useSearchAddress from 'modules/addresses/hooks/use-search-address';
import {useDebounce} from 'use-debounce';
import Loading from 'shared/migration-components/loading';
import {CoordinatesProps} from 'shared/types/coordinates';
import formatAddress from 'modules/addresses/util/format-address';
import IconButton from 'shared/migration-components/icon-button';

export interface AddressInputProps<T = any>
  extends Omit<BaseInputProps, 'name'> {
  name: AllKeys<T>;
  index?: number;
  gridColumn?: number;
  placeholder?: string;
  confirmCoordinates?: boolean;
  userId?: string;
  withSearch?: boolean;
}

const AddressInputProps = ({
  withSearch = true,
  ...props
}: AddressInputProps) => {
  const [spotlightStore, spotlight] = useMemo(() => createSpotlight(), []);
  const [opened, setOpened] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const {control} = useFormContext();
  const {field} = useController({
    control,
    name: props.name,
  });
  const [search, setSearch] = useState<string>();
  const [searchAddress] = useDebounce(search, 1000);
  const [addressDraft, setAddressDraft] = useState<AddressProps | undefined>(
    field.value,
  );
  const [selectedAddress, setSelectedAddress] = useState<
    AddressProps | undefined
  >(field.value);

  const {data, isLoading} = useSearchAddress(searchAddress);

  const onSubmit = (data: AddressProps) => {
    if (
      !data.latitude ||
      props.confirmCoordinates ||
      (selectedAddress &&
        JSON.stringify(data) !== JSON.stringify(selectedAddress))
    ) {
      setAddressDraft({
        ...data,
        latitude: data.latitude ?? field.value?.latitude,
        longitude: data.longitude ?? field.value?.longitude,
      });
      setShowMap(true);
    } else {
      setAddressDraft(data);
      field.onChange(data);
    }
    setOpened(false);
  };

  const addAddressManually = () => {
    closeSpotLight();
    setOpened(true);
  };

  const handleAddCoordinates = (coordinates: CoordinatesProps) => {
    const address = {
      ...addressDraft,
      latitude: +coordinates.latitude,
      longitude: +coordinates.longitude,
    };
    field.onChange(address);
    setAddressDraft(address as AddressProps);
    setShowMap(false);
  };

  const openSpotLight = () => {
    spotlight.open();
  };

  const closeSpotLight = () => {
    spotlight.close();
  };

  const resetSearch = () => {
    setOpened(false);
    openSpotLight();
  };

  const getAction = (address: AddressProps): SpotlightActionData => {
    let label = '';
    if (address.street) label = address.street;
    if (address.number) label += `, ${address.number}`;
    if (address.neighborhood)
      label += label ? ` - ${address.neighborhood}` : address.neighborhood;
    if (!address.street)
      label += `${!label ? '' : ' - '}${address.city} - ${address.state}`;
    if (address.name) label = address.name;

    let description: string | undefined;
    if (address.name) description = formatAddress(address);
    if (!address.name && address.street)
      description = `${address.city} - ${address.state}`;

    return {
      id: label,
      label,
      description,
      onClick: () => {
        setSelectedAddress(address);
        setAddressDraft(address);
        if (address.id) return field.onChange(address);
        setOpened(true);
      },
    };
  };

  const actions = data?.map(getAction) ?? [];

  return (
    <>
      <InputButton
        {...{
          ...props,
          objectName: props.name,
          type: 'input-button',
          onClick:
            field.value || !withSearch ? () => setOpened(true) : openSpotLight,
          formatValue: (data) => (data ? formatAddress(data) : ''),
        }}
      />
      {opened && (
        <AddressModal
          {...{
            opened,
            onClose: () => setOpened(false),
            onSubmit,
            title: 'Endereço',
            defaultValues: addressDraft,
            resetSearch,
            withSearch,
          }}
        />
      )}
      <Spotlight
        {...{
          actions,
        }}
        store={spotlightStore}
        searchProps={{leftSection: <IoSearch />, placeholder: 'Pesquisar'}}
        onQueryChange={setSearch}
        filter={(_, actions) => actions}
        closeOnActionTrigger
        nothingFound={
          <CenterdColumn>
            {isLoading && <Loading />}
            {!isLoading && !data && 'Digite para encontrar o endereço'}
            {!isLoading && data?.length === 0 && (
              <>
                Nenhum endereço encontrado...
                <IconButton
                  label="Criar novo"
                  icon={<IoAdd />}
                  variant="filled"
                  onClick={addAddressManually}
                />
              </>
            )}
          </CenterdColumn>
        }
      />
      {showMap && (
        <Map
          onClose={() => setShowMap(false)}
          onConfirm={handleAddCoordinates}
          {...{addressDraft}}
        />
      )}
    </>
  );
};

export default AddressInputProps;
